import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MDRBadge } from "@/components/MDRBadge";
import { CsvIOButtons } from "@/components/admin/CsvIOButtons";
import { toast } from "sonner";
import {
  examTypesData, serviceExtendedData, examTypeStatuses, statusLabels, getExamTypeById
} from "@/data/adminConfigData";
import { servicesData } from "@/data/mockData";
import { getPriceCategoryFromType, getPriceCategoryConfig, formatEUR } from "@/data/billingData";
import { Layers, Plus, Pencil, Trash2, Copy, Euro, Clock, Calendar, Tag } from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  testing: "bg-amber-50 text-amber-700 border-amber-200",
  attivo: "bg-emerald-50 text-emerald-700 border-emerald-200",
  non_attivo: "bg-rose-50 text-rose-700 border-rose-200"
};

// Merge base service + extension
const buildRows = () => servicesData.map(s => {
  const ext = serviceExtendedData.find(e => e.serviceId === s.id);
  return {
    id: s.id,
    name: s.name,
    description: s.description,
    typeId: ext?.typeId || "et1",
    duration: s.duration,
    prezzoPubblico: ext?.prezzoPubblico ?? 0,
    costoGestione: ext?.costoGestione ?? 0,
    costoFarmacia: ext?.costoFarmacia ?? 0,
    costoMedico: ext?.costoMedico ?? 0,
    status: ext?.status || "draft",
    dataAttivazione: ext?.dataAttivazione || "",
    dataScadenza: ext?.dataScadenza || ""
  };
});

const csvColumns = [
  { key: "id", label: "id" },
  { key: "name", label: "nome" },
  { key: "description", label: "descrizione" },
  { key: "typeId", label: "tipologia_id" },
  { key: "duration", label: "durata_min", parse: Number },
  { key: "prezzoPubblico", label: "prezzo_pubblico", parse: Number },
  { key: "costoGestione", label: "costo_gestione", parse: Number },
  { key: "costoFarmacia", label: "costo_farmacia", parse: Number },
  { key: "costoMedico", label: "costo_medico", parse: Number },
  { key: "status", label: "stato" },
  { key: "dataAttivazione", label: "data_attivazione" },
  { key: "dataScadenza", label: "data_scadenza" }
];

const empty = { name: "", description: "", typeId: "et1", duration: 5, prezzoPubblico: 0, costoGestione: 0, costoFarmacia: 0, costoMedico: 0, status: "draft", dataAttivazione: "", dataScadenza: "" };

export const AdminServicesPage = () => {
  const [rows, setRows] = useState(buildRows());
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const startEdit = (r) => { setEditing(r ? { ...r } : { ...empty, id: `srv${Date.now()}` }); setOpen(true); };

  const clone = (r) => {
    const newId = `${r.id}_copy${Date.now().toString().slice(-4)}`;
    setRows(prev => [...prev, { ...r, id: newId, name: `${r.name} (copia)`, status: "draft" }]);
    toast.success("Prestazione clonata", { description: `Stato: bozza, modifica per attivare.` });
  };

  const save = () => {
    if (!editing.name) { toast.error("Inserire nome prestazione"); return; }
    // Enforce Struttura quota = 0 for "instrumental" price category
    const priceCat = getPriceCategoryFromType(editing.typeId);
    const normalized = priceCat === "instrumental"
      ? { ...editing, costoMedico: 0 }
      : editing;
    const sumQuote = (normalized.costoGestione || 0) + (normalized.costoFarmacia || 0) + (normalized.costoMedico || 0);
    if (sumQuote > normalized.prezzoPubblico) {
      toast.error("Le quote eccedono il prezzo cittadino", {
        description: `Somma quote ${sumQuote}€ > prezzo ${normalized.prezzoPubblico}€`
      });
      return;
    }
    setRows(prev => {
      const exists = prev.find(p => p.id === normalized.id);
      return exists ? prev.map(p => p.id === normalized.id ? normalized : p) : [...prev, normalized];
    });
    setOpen(false);
    toast.success("Prestazione salvata");
  };

  const remove = (id) => {
    setRows(prev => prev.filter(p => p.id !== id));
    toast.success("Prestazione rimossa");
  };

  const handleImport = (parsed) => {
    setRows(parsed.map(p => ({ ...empty, ...p })));
  };

  const filtered = rows.filter(r =>
    (filterType === "all" || r.typeId === filterType) &&
    (filterStatus === "all" || r.status === filterStatus)
  );

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Layers className="h-4 w-4 text-slate-600" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">6.2 Configurazione</span>
            <MDRBadge variant="admin" size="sm" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Prestazioni</h1>
          <p className="text-sm text-muted-foreground mt-1">Catalogo prestazioni con pricing e ripartizione costi</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CsvIOButtons rows={rows} columns={csvColumns} filename="prestazioni" onImport={handleImport} testIdPrefix="services-csv" />
          <Button variant="premium" onClick={() => startEdit(null)} className="gap-2" data-testid="services-new-btn">
            <Plus className="h-4 w-4" /> Nuova prestazione
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg border bg-muted/20">
        <Tag className="h-4 w-4 text-muted-foreground" />
        <select className="border rounded-md p-1.5 text-sm bg-background" value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="services-filter-type">
          <option value="all">Tutte le tipologie</option>
          {examTypesData.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className="border rounded-md p-1.5 text-sm bg-background" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="services-filter-status">
          <option value="all">Tutti gli stati</option>
          {examTypeStatuses.map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
        </select>
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} di {rows.length}</span>
      </div>

      {/* Cards */}
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map(r => {
          const type = getExamTypeById(r.typeId);
          const priceCategory = getPriceCategoryFromType(r.typeId);
          const catCfg = getPriceCategoryConfig(priceCategory);
          const isInstrumentalOnly = priceCategory === "instrumental";
          const effectiveStruttura = isInstrumentalOnly ? 0 : r.costoMedico;
          const margin = r.prezzoPubblico - r.costoGestione - r.costoFarmacia - effectiveStruttura;
          return (
            <Card key={r.id} data-testid={`service-card-${r.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">{r.name}</CardTitle>
                  <Badge variant="outline" className={STATUS_STYLE[r.status]}>{statusLabels[r.status]}</Badge>
                </div>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <Badge variant="outline" className={`text-[10px] ${catCfg.color}`} data-testid={`service-category-${r.id}`}>
                    {catCfg.short}
                  </Badge>
                  <span className="text-xs text-muted-foreground">· {type?.name}</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground line-clamp-2">{r.description}</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2 rounded bg-emerald-50 border border-emerald-100">
                    <p className="text-[10px] text-emerald-700 uppercase tracking-wider">Prezzo cittadino</p>
                    <p className="font-bold text-emerald-800 inline-flex items-center gap-1"><Euro className="h-3 w-3" /> {r.prezzoPubblico}</p>
                  </div>
                  <div className="p-2 rounded bg-sky-50 border border-sky-100">
                    <p className="text-[10px] text-sky-700 uppercase tracking-wider">Margine residuo</p>
                    <p className="font-bold text-sky-800 inline-flex items-center gap-1"><Euro className="h-3 w-3" /> {margin}</p>
                  </div>
                </div>
                <div className="space-y-1 text-[11px]">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Ripartizione quote</p>
                  <div className="flex justify-between"><span className="text-violet-700">Quota NewCo</span><span className="font-mono">€ {r.costoGestione}</span></div>
                  <div className="flex justify-between"><span className="text-indigo-700">Quota Farmacia</span><span className="font-mono">€ {r.costoFarmacia}</span></div>
                  <div className="flex justify-between">
                    <span className={isInstrumentalOnly ? "text-muted-foreground" : "text-cyan-700"}>
                      Quota Struttura {isInstrumentalOnly && <span className="text-[9px]">(N/A)</span>}
                    </span>
                    <span className="font-mono">€ {effectiveStruttura}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-2 border-t text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {r.duration}m</span>
                  {r.dataAttivazione && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> Da {r.dataAttivazione}</span>}
                  {r.dataScadenza && <span>→ {r.dataScadenza}</span>}
                </div>
                <div className="flex justify-end gap-1 pt-2 border-t">
                  <Button variant="ghost" size="icon" onClick={() => clone(r)} title="Clona" data-testid={`service-clone-${r.id}`}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => startEdit(r)} data-testid={`service-edit-${r.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(r.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <Card className="md:col-span-2 lg:col-span-3 border-dashed">
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              Nessuna prestazione corrisponde ai filtri.
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing && rows.find(p => p.id === editing.id) ? "Modifica prestazione" : "Nuova prestazione"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nome</Label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="service-form-name" />
                </div>
                <div>
                  <Label>Tipologia</Label>
                  <select className="w-full mt-1 border rounded-md p-2 text-sm bg-background"
                    value={editing.typeId}
                    onChange={(e) => setEditing({ ...editing, typeId: e.target.value })}>
                    {examTypesData.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Descrizione</Label>
                <Textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Durata stimata (min)</Label>
                  <Input type="number" min="1" value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: Number(e.target.value) })} />
                </div>
                <div>
                  <Label>Stato</Label>
                  <select className="w-full mt-1 border rounded-md p-2 text-sm bg-background"
                    value={editing.status}
                    onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                    {examTypeStatuses.map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 p-3 rounded-md bg-emerald-50/30 border border-emerald-100">
                <div className="col-span-2 -mb-1">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">
                    Listino · Ripartizione quote {(() => {
                      const c = getPriceCategoryConfig(getPriceCategoryFromType(editing.typeId));
                      return <span className={`ml-1 inline-block px-1.5 py-0.5 rounded border text-[10px] ${c.color}`}>{c.label}</span>;
                    })()}
                  </p>
                </div>
                <div>
                  <Label>Prezzo cittadino (€)</Label>
                  <Input type="number" min="0" step="0.5" value={editing.prezzoPubblico} onChange={(e) => setEditing({ ...editing, prezzoPubblico: Number(e.target.value) })} data-testid="service-form-price" />
                </div>
                <div>
                  <Label>Quota NewCo (€)</Label>
                  <Input type="number" min="0" step="0.5" value={editing.costoGestione} onChange={(e) => setEditing({ ...editing, costoGestione: Number(e.target.value) })} data-testid="service-form-newco" />
                </div>
                <div>
                  <Label>Quota Farmacia (€)</Label>
                  <Input type="number" min="0" step="0.5" value={editing.costoFarmacia} onChange={(e) => setEditing({ ...editing, costoFarmacia: Number(e.target.value) })} data-testid="service-form-farmacia" />
                </div>
                <div>
                  <Label>
                    Quota Struttura (€)
                    {getPriceCategoryFromType(editing.typeId) === "instrumental" && (
                      <span className="ml-1 text-[10px] text-amber-700">forzata a 0 per esame strumentale</span>
                    )}
                  </Label>
                  <Input
                    type="number" min="0" step="0.5"
                    value={getPriceCategoryFromType(editing.typeId) === "instrumental" ? 0 : editing.costoMedico}
                    disabled={getPriceCategoryFromType(editing.typeId) === "instrumental"}
                    onChange={(e) => setEditing({ ...editing, costoMedico: Number(e.target.value) })}
                    data-testid="service-form-struttura"
                  />
                </div>
                <div className="col-span-2 text-[11px] text-muted-foreground">
                  Somma quote: <span className="font-mono">{formatEUR((editing.costoGestione || 0) + (editing.costoFarmacia || 0) + (getPriceCategoryFromType(editing.typeId) === "instrumental" ? 0 : (editing.costoMedico || 0)))}</span>
                  {" · "}Prezzo cittadino: <span className="font-mono">{formatEUR(editing.prezzoPubblico)}</span>
                  {(() => {
                    const sum = (editing.costoGestione || 0) + (editing.costoFarmacia || 0) + (getPriceCategoryFromType(editing.typeId) === "instrumental" ? 0 : (editing.costoMedico || 0));
                    if (sum > editing.prezzoPubblico) return <span className="ml-1 text-rose-600 font-semibold">· quote eccedono prezzo!</span>;
                    return null;
                  })()}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Data attivazione</Label>
                  <Input type="date" value={editing.dataAttivazione} onChange={(e) => setEditing({ ...editing, dataAttivazione: e.target.value })} />
                </div>
                <div>
                  <Label>Data scadenza</Label>
                  <Input type="date" value={editing.dataScadenza} onChange={(e) => setEditing({ ...editing, dataScadenza: e.target.value })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="service-form-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminServicesPage;
